"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.selectPerformanceOverview = exports.peerPerformanceByOrg = exports.nationalPerformance = void 0;
var _immutable = require("immutable");
var _reselect = require("reselect");
var _dateTime = require("../helpers/dateTime.js");
var _immutableHelpers = require("../helpers/immutableHelpers.js");
var _b2bPeerCalc = require("../helpers/b2bPeerCalc.js");
// Performance Spec: https://github.com/BoardVitals/boardvitals/tree/BV-4613#user-question-status

// Selectors for QuestionBank and User IDs provide default values.
// Make sure to use these selectors in other selectors which need the QuestionBank or User IDs.

var DEFAULT_EMPTY_QUIZ_TYPE_SELECTED = [];
var selectQuestionBankId = function selectQuestionBankId(state, questionBankId) {
  return questionBankId || state.session.get('selected_question_bank_id');
};
var selectUserId = function selectUserId(state, questionBankId, userId) {
  return userId || state.session.get('current_user_id');
};

// We only need specific attributes from the User and QuestionBank so we select them directly to minimize unneeded cache busting.
var selectTutorModeEnabled = function selectTutorModeEnabled(state, questionBankId, userId) {
  return state.users.getIn([selectUserId(state, questionBankId, userId), 'tutor_mode_enabled'], false);
};
var selectPeerRankThreshold = function selectPeerRankThreshold(state, questionBankId) {
  return state.questionBanks.getIn([selectQuestionBankId(state, questionBankId), 'peer_rank_threshold'], 100);
};
var setSelectedQuizTypes = function setSelectedQuizTypes(state, questionBankId, userId, allowFilter) {
  return allowFilter ? state.users.getIn([selectUserId(state, questionBankId, userId), 'last_quiz_types_selected'], DEFAULT_EMPTY_QUIZ_TYPE_SELECTED) : DEFAULT_EMPTY_QUIZ_TYPE_SELECTED;
};
var selectPerformanceOverview = exports.selectPerformanceOverview = (0, _reselect.createSelector)([function (state, questionBankId) {
  return selectQuestionBankId(state, questionBankId);
}, function (state, questionBankId, userId) {
  return selectUserId(state, questionBankId, userId);
}, function (state, questionBankId, userId) {
  return selectTutorModeEnabled(state, questionBankId, userId);
}, function (state, questionBankId) {
  return selectPeerRankThreshold(state, questionBankId);
}, function (state, questionBankId, userId, allowFilter) {
  return setSelectedQuizTypes(state, questionBankId, userId, allowFilter);
}, function (state) {
  return state.subscriptions;
}, function (state) {
  return state.organizationalMemberships;
}, function (state) {
  return state.organizationalSubscriptions;
}, function (state) {
  return state.performancePeers;
}, function (state) {
  return state.performanceQuestions;
}, function (state) {
  return state.performanceResponses;
}, function (state) {
  return state.quizzes;
}, function (state) {
  return state.questionBanks;
}, function (state) {
  return state.riskAssessment;
},
// NOTE: This assumes that we are still only loading the Subjects for the selected QuestionBank
function (state) {
  return state.subjects;
}], function (questionBankId, userId, tutorModeEnabled, peerRankThreshold, selectedQuizTypes, subscriptions, organizationalMemberships, organizationalSubscriptions, performancePeers, performanceQuestions, performanceResponses, quizzes, questionBanks, riskAssessment, subjects) {
  var serverTimeNow = (0, _dateTime.getServerTimeNow)();
  var filteredSubscriptions = subscriptions.filter(function (s) {
    return s.get('user_id') === userId && s.get('question_bank_id') === questionBankId && s.get('activation_date') <= serverTimeNow;
  });
  var orgIds = organizationalMemberships.filter(function (om) {
    return om.get('user_id') === userId;
  }).map(function (om) {
    return om.get('organization_id');
  }).toSet();
  var filteredOrganizationalSubscriptions = organizationalSubscriptions.filter(function (s) {
    return orgIds.intersect(s.get('organization_ids')).size > 0 && s.get('question_bank_ids').includes(questionBankId);
  });
  var trialSubscription = filteredSubscriptions.size > 0 && !filteredSubscriptions.find(function (subscription) {
    return !subscription.get('trial');
  }) && filteredOrganizationalSubscriptions.size === 0;
  var derivedSubjects = subjects;
  var subjectIds = derivedSubjects.keySeq();
  var riskValues = ['On_Track', 'Needs_Improvement', 'At_Risk', 'Not_Enough_Data'];
  var riskAssessmentForQB = !!riskAssessment && riskAssessment.toList().find(function (assessment) {
    return assessment.get('question_bank_id') === questionBankId && !assessment.get('disabled_qb');
  });
  var subjectLevelRiskAssessment = !!riskAssessmentForQB && riskAssessmentForQB.get('subject_level_risks');

  // Filtered Quizzes are unarchived, non-search, non-practice Quizzes from the selected QuestionBank
  var filteredQuizzes = quizzes.filter(function (quiz) {
    return quiz.get('question_bank_id') === questionBankId && !quiz.get('archived') && quiz.get('type') !== 'search' && !quiz.get('practice_exam_template_id');
  });
  if (tutorModeEnabled && selectedQuizTypes.length > 0) {
    filteredQuizzes = filteredQuizzes.filter(function (quiz) {
      return selectedQuizTypes.includes(quiz.get('type'));
    });
  }
  var excludeAdaptive = function excludeAdaptive(quiz) {
    return selectedQuizTypes.length || quiz.get('type') !== 'adaptive';
  };
  var filterTutor = tutorModeEnabled ? filteredQuizzes : filteredQuizzes.filter(function (quiz) {
    return quiz.get('type') !== 'tutor';
  });
  var gradedQuizzes = filterTutor.filter(function (quiz) {
    return quiz.get('status') === 'complete';
  });
  var gradedClosedBookQuizzes = gradedQuizzes.filter(function (quiz) {
    return quiz.get('type') !== 'study';
  });
  var scoredQuizzes = (tutorModeEnabled ? gradedQuizzes : gradedClosedBookQuizzes).filter(excludeAdaptive);

  // Filter Questions
  var questionStats = performanceQuestions.filter(function (question) {
    // Filter out Questions which belong to the selected QuestionBanks PracticeExam
    if (question.get('practice')) return false;

    // Filter out Questions which don't share any Subjects with the selected QuestionBank
    if (!question.get('subjectIds', new _immutable.List()).some(function (subjectId) {
      return subjectIds.includes(subjectId);
    })) return false;

    // If the User has a Trial Subscription to the selected QuestionBank then filter out Questions which are non-trial
    if (trialSubscription && !question.get('trial')) return false;
    return true;
  }).toJS();

  // Set up default attributes for stats
  Object.values(questionStats).forEach(function (questionStat) {
    questionStat.totalMsSpent = 0;
    questionStat.averageMsSpent = 0;
    questionStat.gradedResponseCount = 0;
    questionStat.gradedTimedResponseCount = 0;
    questionStat.totalTestMsSpent = 0;
    questionStat.averageTestMsSpent = 0;
    questionStat.gradedTestTimedResponseCount = 0;
    questionStat.gradedCorrectResponseCount = 0;
    questionStat.gradedPartialCorrectResponseCount = 0;
    questionStat.answered = false;
    questionStat.answeredTest = false;
    questionStat.latestGradedCorrect = null;
    questionStat.latestGradedPartiallyCorrect = null;
    questionStat.latestGradedAt = null;
    questionStat.latestGradedPartiallyCorrectClosedBook = null;
    questionStat.latestGradedCorrectClosedBook = null;
    questionStat.latestGradedAtClosedBook = null;
    questionStat.latestGradedCorrectScored = null;
    questionStat.latestGradedPartiallyCorrectScored = null;
    questionStat.latestGradedAtScored = null;
    questionStat.possiblePoints = 0;
    questionStat.actualPoints = 0;
    questionStat.isNgn = false;
  });
  var subjectStats = {};
  subjectIds.forEach(function (subjectId) {
    subjectStats[subjectId] = {
      subjectId: subjectId,
      totalMsSpent: 0,
      averageMsSpent: 0,
      gradedTimedResponseCount: 0,
      totalTestMsSpent: 0,
      averageTestMsSpent: 0,
      gradedTestTimedResponseCount: 0,
      gradedTestResponseCount: 0,
      gradedPartialCorrectCount: 0,
      gradedCorrectResponseCount: 0,
      answeredCount: 0,
      correctCount: 0,
      incorrectCount: 0,
      partialCorrectCount: 0,
      answeredCountTest: 0,
      correctCountTest: 0,
      incorrectCountTest: 0,
      partialCorrectCountTest: 0,
      questionCount: 0,
      weightedScore: 0,
      potentialScore: 0,
      actualPoints: 0,
      score: 0,
      subjectQuestionScore: 0,
      riskCategory: 0,
      riskExplanation: ''
    };
  });
  var quizScores = {};
  quizzes.forEach(function (quiz, quizId) {
    quizScores[quizId] = null;
  });
  var quizPoints = {};
  quizzes.forEach(function (quiz, quizId) {
    quizPoints[quizId] = null;
  });
  var totalMsSpent = 0;
  var averageMsSpent = 0;
  var gradedTimedResponseCount = 0;
  var totalTestMsSpent = 0;
  var averageTestMsSpent = 0;
  var gradedTestTimedResponseCount = 0;
  var answeredCount = 0;
  var correctCount = 0;
  var incorrectCount = 0;
  var partialCorrectCount = 0;
  var answeredCountTest = 0;
  var correctCountTest = 0;
  var incorrectCountTest = 0;
  var partialCorrectCountTest = 0;
  var questionCount = Object.keys(questionStats).length;
  var averageScore = 0;
  var seenCount = 0;
  var scoreAnsweredCount = 0;
  var scoreCorrectCount = 0;
  var possiblePoints = 0;
  var actualPoints = 0;
  var allQuizzesPossiblePoints = 0;
  var allQuizzesActualPoints = 0;

  /*
   PerformanceResponse Format =
   [
    ms_spent,
    extra_ms_spent,
    answered_at,
    correct,
    answered,
    evaluated_at
  ]
   */

  // Extract data from PerformanceResponses into QuestionStats
  performanceResponses.forEach(function (responsesByQuestionId, quizIdStr) {
    var quizId = parseInt(quizIdStr);
    var isClosedBook = gradedClosedBookQuizzes.has(quizId);
    var isScored = scoredQuizzes.has(quizId);
    var isCompleted = gradedQuizzes.has(quizId);
    var quizQuestionCount = quizzes.getIn([quizId, 'total_questions'], 0);
    // eslint-disable-next-line no-unused-vars
    var quizCorrectCount = 0;
    // eslint-disable-next-line no-unused-vars
    var quizPartialCorrectCount = 0;
    var quizPossiblePoints = 0;
    var quizActualPoints = 0;
    responsesByQuestionId.forEach(function (response, questionId) {
      if (response.get(6)) {
        if (response.get(9) === 2) quizCorrectCount += 1;
        if (response.get(9) === 3) quizPartialCorrectCount += 1;
        if (response.get(9) !== 0 || isCompleted) {
          quizPossiblePoints += response.get(7);
          quizActualPoints += response.get(8);
        }
      } else {
        if (response.get(3)) {
          quizCorrectCount += 1;
          quizActualPoints += 1;
        }
        quizPossiblePoints += 1;
      }
      var questionStat = questionStats[questionId];
      if (!questionStat) return;
      if (response.get(6)) questionStat.isNgn = true;
      // Question is Seen if ANY Response has time spent or has been answered.
      // We have to check responses instead of relying on the `seen` value from the Performance#questions endpoint for
      // backwards compatibility with Responses that predate the users_seen_questions table.
      // TODO: Remove this line once BV-4615 is completed
      if (response.get(0) > 0 || response.get(1) > 0 || response.get(4)) questionStat.seen = true;
      // Remaining statistics do not include unanswered responses or unfiltered Quizzes
      if (!response.get(10) || !response.get(4) || !filteredQuizzes.has(quizId)) return;
      if (gradedClosedBookQuizzes.has(quizId)) questionStat.answeredTest = true;
      questionStat.answered = true;

      // Remaining statistics do not include incomplete Quizzes
      if (!isCompleted) return;

      // At this point the Response is considered Graded
      questionStat.gradedResponseCount += 1;
      if (questionStat.latestGradedAt === null || questionStat.latestGradedAt < response.get(2)) {
        questionStat.latestGradedAt = response.get(2);
        if (response.get(6) === true) {
          if (response.get(9) === 2) {
            questionStat.latestGradedCorrect = true;
            questionStat.latestGradedPartiallyCorrect = false;
          } else if (response.get(9) === 3) {
            questionStat.latestGradedCorrect = false;
            questionStat.latestGradedPartiallyCorrect = true;
          } else if (response.get(9) === 1) {
            questionStat.latestGradedCorrect = false;
            questionStat.latestGradedPartiallyCorrect = false;
          }
        } else {
          questionStat.latestGradedCorrect = response.get(3);
          questionStat.latestGradedPartiallyCorrect = false;
        }
      }
      if (isClosedBook && (questionStat.latestGradedAtClosedBook === null || questionStat.latestGradedAtClosedBook < response.get(2))) {
        questionStat.latestGradedAtClosedBook = response.get(2);
        if (response.get(6) === true) {
          if (response.get(9) === 2) {
            questionStat.latestGradedCorrectClosedBook = true;
            questionStat.latestGradedPartiallyCorrectClosedBook = false;
          }
          if (response.get(9) === 3) {
            questionStat.latestGradedPartiallyCorrectClosedBook = true;
            questionStat.latestGradedCorrectClosedBook = false;
          }
          if (response.get(9) === 1) {
            questionStat.latestGradedPartiallyCorrectClosedBook = false;
            questionStat.latestGradedCorrectClosedBook = false;
          }
        } else {
          questionStat.latestGradedCorrectClosedBook = response.get(3);
          questionStat.latestGradedPartiallyCorrectClosedBook = false;
        }
      }
      if (isScored && (questionStat.latestGradedAtScored === null || questionStat.latestGradedAtScored < response.get(2))) {
        questionStat.latestGradedAtScored = response.get(2);
        if (response.get(6)) {
          if (response.get(9) === 2) questionStat.latestGradedCorrectScored = true;
          if (response.get(9) === 3) {
            questionStat.latestGradedPartiallyCorrectScored = true;
            questionStat.latestGradedCorrectScored = false;
          }
        } else {
          questionStat.latestGradedCorrectScored = response.get(3);
          questionStat.latestGradedPartiallyCorrectScored = false;
        }
      }
      questionStat.possiblePoints = performanceQuestions.getIn([parseInt(questionId), 'potentialScore']);
      questionStat.actualPoints = response.get(8);

      // Graded Response has timing data
      if (response.get(0)) {
        totalMsSpent += response.get(0);
        gradedTimedResponseCount += 1;
        questionStat.totalMsSpent += response.get(0);
        questionStat.gradedTimedResponseCount += 1;
        if (isClosedBook) {
          totalTestMsSpent += response.get(0);
          gradedTestTimedResponseCount += 1;
          questionStat.totalTestMsSpent += response.get(0);
          questionStat.gradedTestTimedResponseCount += 1;
        }
      }

      // Skip Responses from Open Book Quizzes unless the User has tutor mode enabled
      if (!tutorModeEnabled && !isClosedBook) return;

      // Graded Response is correct
      if (response.get(6)) {
        if (response.get(9) === 2) questionStat.gradedCorrectResponseCount += 1;
        if (response.get(9) === 3) questionStat.gradedPartialCorrectResponseCount += 1;
      } else if (response.get(3)) {
        questionStat.gradedCorrectResponseCount += 1;
      }
    });
    if (quizQuestionCount > 0) quizScores[quizId] = quizActualPoints / quizPossiblePoints;
    if (quizQuestionCount > 0) quizPoints[quizId] = [quizActualPoints, quizPossiblePoints];
  });

  // Calculate values from QuestionStats
  Object.values(questionStats).forEach(function (questionStat) {
    if (questionStat.gradedTimedResponseCount > 0) questionStat.averageMsSpent = Math.round(questionStat.totalMsSpent / questionStat.gradedTimedResponseCount);
    if (questionStat.gradedTestTimedResponseCount > 0) questionStat.averageTestMsSpent = Math.round(questionStat.totalTestMsSpent / questionStat.gradedTestTimedResponseCount);
    if (questionStat.seen) seenCount += 1;
    if (questionStat.answered) answeredCount += 1;
    if (questionStat.answeredTest) answeredCountTest += 1;
    if (questionStat.latestGradedCorrect === true) correctCount += 1;
    if (questionStat.latestGradedPartiallyCorrect === true) partialCorrectCount += 1;
    if (questionStat.latestGradedCorrect === false && questionStat.latestGradedPartiallyCorrect === false) incorrectCount += 1;
    if (questionStat.latestGradedCorrectClosedBook === true) correctCountTest += 1;
    if (questionStat.latestGradedPartiallyCorrectClosedBook === true) partialCorrectCountTest += 1;
    if (questionStat.latestGradedCorrectClosedBook === false && questionStat.latestGradedPartiallyCorrectClosedBook === false) incorrectCountTest += 1;
    if (questionStat.latestGradedCorrectScored === true || questionStat.latestGradedPartiallyCorrectScored === true) scoreCorrectCount += questionStat.actualPoints;
    if (questionStat.latestGradedAtScored) scoreAnsweredCount += questionStat.possiblePoints;
    possiblePoints += questionStat.possiblePoints;
    actualPoints += questionStat.actualPoints;
    // Extract data for SubjectStats
    questionStat.subjectIds.forEach(function (subjectId) {
      var _subjectLevelRiskAsse, _subjectLevelRiskAsse2;
      var subjectStat = subjectStats[subjectId];
      if (!subjectStat) return;
      var riskCategoryForSubject = !!subjectLevelRiskAssessment && ((_subjectLevelRiskAsse = subjectLevelRiskAssessment.find(function (risk) {
        return risk.subjectId === subjectStat.subjectId;
      })) === null || _subjectLevelRiskAsse === void 0 ? void 0 : _subjectLevelRiskAsse.riskCategory);
      var riskExplanationForSubject = !!subjectLevelRiskAssessment && ((_subjectLevelRiskAsse2 = subjectLevelRiskAssessment.find(function (risk) {
        return risk.subjectId === subjectStat.subjectId;
      })) === null || _subjectLevelRiskAsse2 === void 0 ? void 0 : _subjectLevelRiskAsse2.explanation);
      subjectStat.questionCount += 1;
      subjectStat.totalMsSpent += questionStat.totalMsSpent;
      subjectStat.gradedResponseCount += questionStat.gradedResponseCount;
      subjectStat.totalTestMsSpent += questionStat.totalTestMsSpent;
      subjectStat.gradedTestResponseCount += questionStat.gradedTestResponseCount;
      subjectStat.gradedPartialCorrectCount += questionStat.gradedPartialCorrectResponseCount;
      subjectStat.gradedTimedResponseCount += questionStat.gradedTimedResponseCount;
      subjectStat.gradedTestTimedResponseCount += questionStat.gradedTestTimedResponseCount;
      subjectStat.gradedCorrectResponseCount += questionStat.gradedCorrectResponseCount;
      subjectStat.riskCategory = riskValues.indexOf(riskCategoryForSubject);
      subjectStat.riskExplanation = riskExplanationForSubject;
      if (questionStat.answered) subjectStat.answeredCount += 1;
      if (questionStat.answeredTest) subjectStat.answeredCountTest += 1;
      if (questionStat.latestGradedCorrect === true) subjectStat.correctCount += 1;
      if (questionStat.latestGradedPartiallyCorrect === true) subjectStat.partialCorrectCount += 1;
      if (questionStat.latestGradedCorrect === false && questionStat.latestGradedPartiallyCorrect === false) subjectStat.incorrectCount += 1;
      if (questionStat.latestGradedCorrectClosedBook === true) subjectStat.correctCountTest += 1;
      if (questionStat.latestGradedPartiallyCorrectClosedBook === true) subjectStat.partialCorrectCountTest += 1;
      if (questionStat.latestGradedCorrectClosedBook === false && questionStat.latestGradedPartiallyCorrectClosedBook === false) subjectStat.incorrectCountTest += 1;
      subjectStat.potentialScore += questionStat.possiblePoints;
      subjectStat.actualPoints += questionStat.actualPoints;
      subjectStat.score += questionStat.actualPoints;
    });
  });

  // Calculate values from SubjectStats
  Object.values(subjectStats).forEach(function (subjectStat) {
    if (subjectStat.gradedTimedResponseCount > 0) subjectStat.averageMsSpent = Math.round(subjectStat.totalMsSpent / subjectStat.gradedTimedResponseCount);
    if (subjectStat.gradedTestTimedResponseCount > 0) subjectStat.averageTestMsSpent = Math.round(subjectStat.totalTestMsSpent / subjectStat.gradedTestTimedResponseCount);
    if (subjectStat.gradedResponseCount > 0) subjectStat.weightedScore = Math.round(subjectStat.gradedCorrectResponseCount / subjectStat.gradedResponseCount * 100);
    if (subjectStat.correctCount + subjectStat.incorrectCount > 0) subjectStat.subjectQuestionScore = Math.round(subjectStat.correctCount / (subjectStat.correctCount + subjectStat.incorrectCount) * 100);
    subjectStat.unansweredCount = subjectStat.questionCount - subjectStat.answeredCount;
    subjectStat.unansweredCountTest = subjectStat.questionCount - subjectStat.answeredCountTest;
  });

  // Question Score is the number of unique Questions where the most recent eligible Response was correct
  // divided by the total number of unique Questions with an eligible answered Response.
  var questionScore = scoreAnsweredCount > 0 ? Math.round(scoreCorrectCount / scoreAnsweredCount * 100) : 0;

  // Average Score is the average score attribute from all eligible Quizzes.
  if (scoredQuizzes.size > 0) {
    averageScore = scoredQuizzes.reduce(function (sum, quiz) {
      return sum + quizScores[quiz.get('id')];
    }, 0);
    averageScore /= scoredQuizzes.size;
    averageScore = Math.round(averageScore * 100);
    // Get all points from scoredQuizzes for NGN banks
    scoredQuizzes.forEach(function (quiz) {
      if (quizPoints[quiz.get('id')]) {
        allQuizzesPossiblePoints += quizPoints[quiz.get('id')][1];
        allQuizzesActualPoints += quizPoints[quiz.get('id')][0];
      }
    });
  }

  // Calculate Average MS Spent
  if (gradedTimedResponseCount > 0) averageMsSpent = totalMsSpent / gradedTimedResponseCount;
  if (gradedTestTimedResponseCount > 0) averageTestMsSpent = totalTestMsSpent / gradedTestTimedResponseCount;

  // Calculate Peer Rank
  // TODO: Update this to support multiple Practice Exams per QuestionBank
  var peerRank;
  var peerAverage;
  var peerStandardDeviation;

  // Peer Rank uses different scores depending on whether or not Tutor Mode is enabled.
  // The backend handles this automatically for performancePeers.
  var peerScore = tutorModeEnabled ? questionScore : averageScore;
  if (peerScore) {
    var totalPeers = 1;
    var lesserPeers = 0;
    var totalPeerScores = peerScore;
    performancePeers.forEach(function (performancePeer) {
      if (performancePeer.get('practice_exam_template_id')) return;
      totalPeers += performancePeer.get('users_count');
      totalPeerScores += performancePeer.get('score') * performancePeer.get('users_count');
      if (performancePeer.get('score') <= peerScore) lesserPeers += performancePeer.get('users_count');
    });
    if (totalPeers > peerRankThreshold) {
      peerAverage = (totalPeerScores + peerScore) / totalPeers;
      peerRank = Math.round(lesserPeers / (totalPeers - 1) * 100);

      // Calculate Standard Deviation
      var totalDeviation = performancePeers.reduce(function (sum, performancePeer) {
        var deviation = Math.pow(performancePeer.get('score') - peerAverage, 2);
        return sum + deviation * performancePeer.get('users_count');
      }, 0) + Math.pow(peerScore - peerAverage, 2);
      peerStandardDeviation = Math.sqrt(totalDeviation / totalPeers);
    }
  }
  var latestScore = answeredCount > 0 ? Math.round(correctCount / answeredCount * 100) : 0;
  var latestScoreTest = answeredCountTest > 0 ? Math.round(correctCountTest / answeredCountTest * 100) : 0;
  return new _immutable.Map({
    averageScore: averageScore,
    questionScore: questionScore,
    latestScore: latestScore,
    latestScoreTest: latestScoreTest,
    actualPoints: actualPoints,
    possiblePoints: possiblePoints,
    allQuizzesPossiblePoints: allQuizzesPossiblePoints,
    allQuizzesActualPoints: allQuizzesActualPoints,
    quizCount: gradedQuizzes.size,
    questionCount: questionCount,
    seenCount: seenCount,
    unseenCount: questionCount - seenCount,
    answeredCount: answeredCount,
    answeredCountTest: answeredCountTest,
    unansweredCount: questionCount - answeredCount,
    unansweredCountTest: questionCount - answeredCountTest,
    correctCount: correctCount,
    incorrectCount: incorrectCount,
    partialCorrectCount: partialCorrectCount,
    correctCountTest: correctCountTest,
    incorrectCountTest: incorrectCountTest,
    partialCorrectCountTest: partialCorrectCountTest,
    averageMsSpent: averageMsSpent,
    averageTestMsSpent: averageTestMsSpent,
    peerRank: peerRank,
    peerAverage: peerAverage,
    peerStandardDeviation: peerStandardDeviation,
    subjectPerformances: (0, _immutableHelpers.idObjectToNestedMaps)(subjectStats),
    questionPerformances: (0, _immutableHelpers.idObjectToNestedMaps)(questionStats),
    scoreCorrectCount: scoreCorrectCount,
    scoreAnsweredCount: scoreAnsweredCount
  });
});
var bankUsageDefault = {
  peerRank: null,
  peerScore: null,
  peerStandardDeviation: null
};
var selectBankUsage = function selectBankUsage(state, bankUsage) {
  return bankUsage || bankUsageDefault;
};
var peerPerformanceByOrg = exports.peerPerformanceByOrg = (0, _reselect.createSelector)([function (state, questionBankId) {
  return selectQuestionBankId(state, questionBankId);
}, selectUserId, selectTutorModeEnabled, selectPeerRankThreshold, function (state, questionBankId, bankUsage) {
  return selectBankUsage(state, bankUsage);
}, function (state) {
  return state.performanceOrgPeers;
}], function (questionBankId, userId, tutorModeEnabled, peerRankThreshold, bankUsage, performanceOrgPeers) {
  var averageScoreTest = Math.round(bankUsage.total_actual_score_test / bankUsage.total_potential_score_test * 100);
  var averageScoreAll = Math.round(bankUsage.total_actual_score / bankUsage.total_potential_score * 100);
  // Peer Rank uses different scores depending on whether or not Tutor Mode is enabled.
  // The backend handles this automatically for performancePeers.
  var testOrgPeers = performanceOrgPeers.filter(function (p) {
    return p.get('exclude_study');
  });
  var allOrgPeers = performanceOrgPeers.filter(function (p) {
    return !p.get('exclude_study');
  });
  var allResults = (0, _b2bPeerCalc.b2bPeerCalc)(averageScoreAll, allOrgPeers, peerRankThreshold);
  var testResults = (0, _b2bPeerCalc.b2bPeerCalc)(averageScoreTest, testOrgPeers, peerRankThreshold);
  return new _immutable.Map({
    peerRankAll: allResults[0],
    peerScoreAll: averageScoreAll,
    peerStandardDeviationAll: allResults[1],
    peerRankTest: testResults[0],
    peerScoreTest: averageScoreTest,
    peerStandardDeviationTest: testResults[1]
  });
});
var nationalPerformance = exports.nationalPerformance = (0, _reselect.createSelector)([selectTutorModeEnabled, selectPeerRankThreshold, function (state) {
  return state.performanceAllStudents;
}], function (tutorModeEnabled, peerRankThreshold, performanceAllStudents) {
  // Calculate National averageScore
  var totalStudentsAll = 0;
  var totalStudentsScoresAll = 0;
  var totalStudentsTest = 0;
  var totalStudentsScoresTest = 0;
  var testStudents = performanceAllStudents.filter(function (p) {
    return p.get('exclude_study');
  });
  var allStudents = performanceAllStudents.filter(function (p) {
    return !p.get('exclude_study');
  });
  allStudents.forEach(function (student) {
    if (student.get('practice_exam_template_id')) return;
    totalStudentsAll += student.get('users_count');
    totalStudentsScoresAll += student.get('score') * student.get('users_count');
  });
  testStudents.forEach(function (student) {
    if (student.get('practice_exam_template_id')) return;
    totalStudentsTest += student.get('users_count');
    totalStudentsScoresTest += student.get('score') * student.get('users_count');
  });
  var averageScoreAll = Math.round(totalStudentsScoresAll / totalStudentsAll);
  var averageScoreTest = Math.round(totalStudentsScoresTest / totalStudentsTest);
  var allResults = (0, _b2bPeerCalc.b2bPeerCalc)(averageScoreAll, allStudents, peerRankThreshold);
  var testResults = (0, _b2bPeerCalc.b2bPeerCalc)(averageScoreTest, testStudents, peerRankThreshold);
  return new _immutable.Map({
    nationalRankAll: allResults[0],
    nationalScoreAll: averageScoreAll,
    nationalStandardDeviationAll: allResults[1],
    nationalRankTest: testResults[0],
    nationalScoreTest: averageScoreTest,
    nationalStandardDeviationTest: testResults[1]
  });
});